import { useEffect, useState, useRef } from 'react'
import { FormProvider, useForm, type FieldErrors } from 'react-hook-form'
import { useMediaQuery } from '@mui/material'

import { BaseModal2 } from '@components/modals/BaseModal2'
import { ReturnModal } from '@components/modals/ReturnModal'
import { emotionTheme } from '@styles/theme'
import { Input } from '@components/shared/Input'
import { WarningIcon } from '@assets/svgs/WarningIcon'
import { BankIcon } from '@assets/svgs/BankIcon'
import { PencilSimpleIcon } from '@phosphor-icons/react'
import { unMaskCurrency } from '@utils/masks/unMaskCurrency'
import { editFgtsModalSchema } from './form/validations'
import { initialFormValues } from './form/initialFormValues'
import type { EditFgtsModalFormProps } from './form/types'
import type { EditFgtsModalProps } from './types'

import * as S from './styles'

export const EditFgtsModal = ({
  open,
  onClose,
  currentValue,
  onConfirm,
  isLoading = false
}: EditFgtsModalProps) => {
  const isMobile = useMediaQuery(`(max-width: ${emotionTheme.screenSizes.laptop})`)
  const [openCancelModal, setOpenCancelModal] = useState(false)
  const formRef = useRef<HTMLFormElement>(null)

  const methods = useForm<EditFgtsModalFormProps>({
    resolver: editFgtsModalSchema,
    defaultValues: initialFormValues
  })

  // Pré-popular formulário quando modal abre
  useEffect(() => {
    if (!open) {
      methods.reset(initialFormValues)
      return
    }

    if (currentValue) {
      // Formatar o valor em reais diretamente, não como centavos
      const formattedValue = new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(currentValue)
      methods.reset({
        value: formattedValue
      })
    }
  }, [open, currentValue, methods])

  const onError = (errors: FieldErrors<EditFgtsModalFormProps>) => {
    console.log('Erros de validação:', errors)
  }

  const onSubmit = async () => {
    const formData = methods.getValues()
    // O valor já deve estar como string numérica (ex: "40.00") devido ao onBlur
    // Mas vamos garantir que seja convertido corretamente
    const value = Number(formData.value) || unMaskCurrency(formData.value)

    if (isNaN(value) || value <= 0) {
      console.error('Valor inválido:', value)
      return
    }

    try {
      await onConfirm(value)
      methods.reset(initialFormValues)
      onClose(false)
    } catch (error) {
      console.error('Erro ao salvar:', error)
    }
  }

  // Interceptar fechamento do modal para verificar alterações não salvas
  const { isDirty } = methods.formState

  const handleClose = (value: boolean) => {
    if (isDirty && !value) {
      setOpenCancelModal(true)
    } else {
      methods.reset(initialFormValues)
      onClose(value)
    }
  }

  return (
    <>
      <BaseModal2
        open={open}
        title="Encargos"
        headerIcon={<BankIcon />}
        onClose={handleClose}
        width={isMobile ? 'calc(100% - 20px)' : 600}
        heightContainer="auto"
        maxHeightContainer={isMobile ? 'calc(100% - 20px)' : '95%'}
        footer={{
          cancelButtonText: 'Cancelar',
          cancelButtonMinWidth: isMobile ? 'calc(50% - 8px)' : 193.5,
          confirmButtonText: 'Confirmar',
          confirmButtonMinWidth: isMobile ? 'calc(50% - 8px)' : 193.5,
          onConfirm: () => {
            // Disparar submit do form
            if (formRef.current) {
              formRef.current.requestSubmit()
            } else {
              methods.handleSubmit(onSubmit, onError)()
            }
          },
          loading: isLoading
        }}
      >
        <FormProvider {...methods}>
          <form ref={formRef} onSubmit={methods.handleSubmit(onSubmit, onError)}>
            <S.FormContainer>
              {/* Título com ícone de lápis */}
              <S.TitleSection>
                <S.TitleBox>
                  <PencilSimpleIcon size={20} weight="bold" color="#2D3748" />
                  <S.TitleText>Editar FGTS Art. 22</S.TitleText>
                </S.TitleBox>
              </S.TitleSection>

              {/* Banner de aviso laranja */}
              <S.WarningBanner>
                <WarningIcon />
                <S.WarningText>
                  O valor inserido na edição, irá substituir o valor calculado automaticamente pelo sistema.
                </S.WarningText>
              </S.WarningBanner>

              {/* Layout lado a lado: informação à esquerda, input de valor à direita */}
              <S.ContentGrid>
                <S.LeftContent>
                  <S.ItemLabel>FGTS Art. 22</S.ItemLabel>
                  <S.ItemCode>64109301</S.ItemCode>
                </S.LeftContent>
                <Input
                  label="Valor (R$)"
                  name="value"
                  placeholder="Insira o valor"
                  maskType="currency"
                  required
                  onBlur={(e) => {
                    const target = e.target as HTMLInputElement
                    if (target.value) {
                      // Converter o valor formatado para número e salvar como string numérica
                      // Seguindo o padrão do EditDetailedSummaryPeopleModal
                      const numericValue = unMaskCurrency(target.value)
                      // Salvar como string numérica para garantir conversão correta
                      methods.setValue('value', numericValue.toFixed(2), { shouldValidate: true })
                    }
                  }}
                />
              </S.ContentGrid>
            </S.FormContainer>
          </form>
        </FormProvider>
      </BaseModal2>

      {/* Modal de alterações não salvas */}
      <ReturnModal
        open={openCancelModal}
        headerIcon={<WarningIcon />}
        headerTitle="Alterações não salvas"
        message="Ao confirmar, os dados inseridos não serão salvos."
        cancelButtonText="Cancelar"
        confirmButtonText="Confirmar"
        onClose={setOpenCancelModal}
        onConfirm={() => {
          setOpenCancelModal(false)
          methods.reset(initialFormValues)
          onClose(false)
        }}
      />
    </>
  )
}
